import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  // Collection-only, scroll-driven loading. Other paginated lists retain their existing behavior.
  #scrollArmed = false;
  #scrollLoading = false;
  #filterPending = false;
  #scrollGeneration = 0;
  #lastScrollY = 0;

  get #loadOnScroll() {
    return this.hasAttribute('load-on-scroll');
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (this.#loadOnScroll) {
      if (typeof IntersectionObserver !== 'undefined') {
        // A batch number is not a stable return position in a changing collection.
        // Only full-document visits normalize the URL; section requests still use page.
        const url = new URL(window.location.href);
        if (url.searchParams.has('page') && !url.searchParams.has('section_id')) {
          url.searchParams.delete('page');
          url.hash = '';
          window.location.replace(url.toString());
          return;
        }
        history.scrollRestoration = 'manual';
        this.#returnToStart();
        window.addEventListener('pageshow', this.#returnToStart);
      }
      this.#resetScrollLoading();
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
    } else {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollGeneration++;
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('pageshow', this.#returnToStart);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    // Facet morphs can replace the sentinels even when the page count stays the same.
    if (this.#loadOnScroll) this.#resetScrollLoading();
  }

  #resetScrollLoading() {
    this.#scrollGeneration++;
    this.#scrollArmed = false;
    this.#scrollLoading = false;
    this.#filterPending = false;
    this.#lastScrollY = window.scrollY;
    this.infinityScrollObserver?.disconnect();
    this.refs.grid?.removeAttribute('aria-busy');
    const fallback = this.querySelector('[data-scroll-pagination-fallback]');
    if (fallback instanceof HTMLElement) fallback.hidden = typeof IntersectionObserver !== 'undefined';
    const retry = this.querySelector('[data-scroll-filter-retry]');
    if (retry instanceof HTMLElement) retry.hidden = true;
  }

  // Covers a fresh visit and a document restored from the browser's back/forward cache.
  #returnToStart = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    this.#lastScrollY = 0;
  };

  #handleScroll = () => {
    const moved = window.scrollY !== this.#lastScrollY;
    this.#lastScrollY = window.scrollY;
    if (!moved || this.#scrollArmed || this.#scrollLoading || this.#filterPending) return;
    if (typeof IntersectionObserver === 'undefined') return;
    this.#scrollArmed = true;
    this.#observeViewMore();
  };

  /** @returns {string} Collection, locale, filter and sort identity, excluding pagination. */
  #scrollContext() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    url.hash = '';
    return url.toString();
  }

  /** @param {"previous" | "next"} type */
  async #renderScrollPage(type) {
    if (!this.#scrollArmed || this.#scrollLoading || this.#filterPending) return;
    const { grid } = this.refs;
    const page = this.#getPage(type);
    if (!grid || !page || !this.#shouldUsePage(page)) return;

    const generation = this.#scrollGeneration;
    const context = this.#scrollContext();
    const isCurrent = () =>
      this.isConnected &&
      generation === this.#scrollGeneration &&
      grid === this.refs.grid &&
      context === this.#scrollContext();
    this.#scrollLoading = true;
    this.#scrollArmed = false;
    this.infinityScrollObserver?.disconnect();
    grid.setAttribute('aria-busy', 'true');

    try {
      // The section renderer mutates its URL argument; keep that internal parameter out of browser history.
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(page.url));
      if (!isCurrent()) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = [...parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]')];
      const existingIds = new Set([...grid.querySelectorAll('[ref="cards[]"]')].map((card) => card.id));
      const cards = incoming.filter(
        (card) => Number(card.getAttribute('data-page')) === page.page && !existingIds.has(card.id)
      );
      if (!cards.length) throw new Error('The requested product page did not contain new product cards.');

      const firstCard = grid.querySelector('[ref="cards[]"]');
      const oldTop = firstCard?.getBoundingClientRect().top;
      if (type === 'previous') grid.prepend(...cards);
      else grid.append(...cards);
      super.updatedCallback();
      this.#aspectRatioHelper?.processNewElements();

      const fallback = this.querySelector('[data-scroll-pagination-fallback]');
      const nextFallback = parsed.querySelector('[data-scroll-pagination-fallback]');
      if (fallback instanceof HTMLElement && nextFallback) {
        fallback.replaceChildren(...nextFallback.childNodes);
        fallback.hidden = true;
      }

      // Appended batches do not create URLs or history entries of their own.
      const retry = this.querySelector('[data-scroll-filter-retry]');
      if (retry instanceof HTMLElement) retry.hidden = true;
      if (type === 'previous' && firstCard && oldTop !== undefined) {
        window.scrollTo({ top: window.scrollY + firstCard.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error(error);
      // A failed dynamic request offers a clean restart, not a stale numbered page.
      // Server-rendered pagination remains available when JavaScript/observation is absent.
      const fallback = this.querySelector('[data-scroll-pagination-fallback]');
      if (fallback instanceof HTMLElement) fallback.hidden = true;
      const retry = this.querySelector('[data-scroll-filter-retry]');
      if (retry instanceof HTMLElement) {
        retry.setAttribute('href', this.#scrollContext());
        retry.hidden = false;
      }
    } finally {
      if (isCurrent()) {
        this.#scrollLoading = false;
        this.#lastScrollY = window.scrollY;
        grid.removeAttribute('aria-busy');
      }
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Recompute the document-root lead when each scroll-driven batch is armed.
    // Pixel units use the visible height; rootMargin percentages resolve to width.
    if (!this.infinityScrollObserver || this.#loadOnScroll) {
      this.infinityScrollObserver?.disconnect();
      const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
      const nextPageLead = Math.round(Math.min(1000, Math.max(320, window.innerHeight - headerHeight)));
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: this.#loadOnScroll ? `100px 0px ${nextPageLead}px 0px` : '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error(error);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#loadOnScroll) return this.#renderScrollPage('next');
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    if (this.#loadOnScroll) return this.#renderScrollPage('previous');
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    if (this.#loadOnScroll) {
      // Invalidate an in-flight response before the new filtered grid arrives.
      this.#scrollGeneration++;
      this.#filterPending = true;
      this.#scrollArmed = false;
      this.infinityScrollObserver?.disconnect();
      // A failed filter request does not morph the grid. Keep a full-page retry reachable.
      const retry = this.querySelector('[data-scroll-filter-retry]');
      if (retry instanceof HTMLElement) {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        retry.setAttribute('href', url.toString());
        retry.hidden = false;
      }
      return;
    }

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
